import { useCallback, useMemo } from "react";
import {
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import useRolesPipeline from "@/features/users/api/useRolesPipeline";
import { roleAcceptsBranchless } from "@/features/leads/hooks/useAssignationPermissions";

/**
 * Collect lead rows from every React Query cache whose key starts with
 * `["leads", ...]` — main list (`infinite`), dynamic stage grid (`stage-route`),
 * kanban (`board` / `stage-board`), etc. Assign eligibility must see the same
 * `branchId` / `permissionItemIds` the list already loaded.
 */
function extractLeadsFromLeadQueriesCache(queryClient: QueryClient): any[] {
  const byId = new Map<number, any>();
  const addLead = (l: any) => {
    const id = Number(l?.id);
    if (!Number.isFinite(id) || id <= 0) return;
    if (!byId.has(id)) byId.set(id, l);
  };

  const entries = queryClient.getQueriesData<any>({
    predicate: (query) =>
      Array.isArray(query.queryKey) && query.queryKey[0] === "leads",
  });

  for (const [, data] of entries) {
    if (!data) continue;

    // useInfiniteQuery: { pages: [{ leads | data | stageLeads }] }
    if (Array.isArray(data.pages)) {
      for (const page of data.pages) {
        const items = page?.leads ?? page?.data ?? page?.stageLeads ?? [];
        if (Array.isArray(items)) items.forEach(addLead);
      }
      continue;
    }

    // Flat { leads } (defensive — some callers may cache this shape)
    if (Array.isArray(data.leads)) {
      data.leads.forEach(addLead);
      continue;
    }

    // useQuery board: BoardColumn[] with .leads
    if (Array.isArray(data)) {
      for (const col of data) {
        if (Array.isArray(col?.leads)) col.leads.forEach(addLead);
      }
    }
  }

  return Array.from(byId.values());
}

type EligibilityMap = Record<number, number>;

/** One gated CF item attached to a lead/child, paired with its parent field. */
export type PermissionGate = { customInputId: number; itemId: number };

export type AssignGateLead = {
  /** API is numeric; the lead form can hold branchId as a string. */
  branchId: number | string | null;
  permissionItemIds: number[];
  /**
   * Field-grouped form of `permissionItemIds`. Required for the per-field OR
   * check: a multi-value gate like `Country=[Germany, UK]` is satisfied as
   * soon as the user has access to AT LEAST ONE of the values. The flat
   * `permissionItemIds` can't express that — kept for legacy/back-compat.
   * Mirrors the server's `computeMissingByField` rule
   * ([[feedback_lead_cf_block_narrow_to_slot]]).
   */
  permissionGates?: PermissionGate[];
};

/** Mirrors server `AssignValidator` branch/CF requirements for one lead. */
export function getAssignBlockReason(
  lead: AssignGateLead,
  userBranchIds: Set<number>,
  userCfAccess: Set<number>,
  /**
   * Role-level "Inbound — receive branchless leads" toggle for the candidate
   * user. Mirrors the server's `roleAllowsBranchlessAssignment`: a branchless
   * lead (branchId null/"") is only assignable to roles with this enabled.
   */
  acceptsBranchless = false
): "branch" | "customField" | "noBranch" | null {
  const rawBranch = lead.branchId;
  if (rawBranch == null || rawBranch === "") {
    if (!acceptsBranchless) return "noBranch";
  } else {
    const bid = Number(rawBranch);
    if (!Number.isNaN(bid) && !userBranchIds.has(bid)) return "branch";
  }
  // Per-field OR: a CF gate is satisfied as soon as the user has access to
  // AT LEAST ONE of the values present on the lead's field. Falls back to
  // legacy strict-AND only when the server didn't send `permissionGates`
  // (older payload shape).
  const gates = lead.permissionGates;
  if (gates && gates.length > 0) {
    const byField = new Map<number, number[]>();
    for (const g of gates) {
      const arr = byField.get(g.customInputId) ?? [];
      arr.push(g.itemId);
      byField.set(g.customInputId, arr);
    }
    for (const itemIds of byField.values()) {
      if (!itemIds.some((id) => userCfAccess.has(id))) return "customField";
    }
    return null;
  }
  const requiredItems: number[] = lead.permissionItemIds;
  if (requiredItems.length > 0) {
    if (!requiredItems.every((id: number) => userCfAccess.has(id)))
      return "customField";
  }
  return null;
}

function isLeadEligibleForUser(
  lead: any,
  userBranchIds: Set<number>,
  userCfAccess: Set<number>,
  acceptsBranchless = false
): boolean {
  return (
    getAssignBlockReason(
      {
        branchId: lead?.branchId ?? null,
        permissionItemIds: lead?.permissionItemIds ?? [],
        permissionGates: lead?.permissionGates ?? [],
      },
      userBranchIds,
      userCfAccess,
      acceptsBranchless
    ) === null
  );
}

/**
 * Computes per-user lead assignment eligibility client-side using:
 * - Lead branchId + permissionItemIds from leads cache
 * - User branches + customFieldAccess from roles pipeline
 */
export default function useAssignEligibility(
  selectedLeadIds: (string | number)[]
) {
  const queryClient = useQueryClient();
  const { rolesPipeline } = useRolesPipeline();

  // Build a map of userId → { branchIds, cfAccess, acceptsBranchless } from
  // roles pipeline. `acceptsBranchless` is role-level (the wizard's
  // "Inbound — receive branchless leads" toggle) so it's computed once per
  // role and stamped on each of its users.
  const usersMap = useMemo(() => {
    const map = new Map<
      number,
      { branchIds: Set<number>; cfAccess: Set<number>; acceptsBranchless: boolean }
    >();
    if (!rolesPipeline) return map;

    for (const role of rolesPipeline) {
      const acceptsBranchless = roleAcceptsBranchless(role);
      for (const user of role.users ?? []) {
        const branchIds = new Set<number>(
          (user.branches ?? [])
            .filter((b: any) => !b.isRevoked)
            .map((b: any) => b.branchId ?? b.branch?.id)
            .map((id: unknown) => Number(id))
            .filter((n: any): n is number => !Number.isNaN(n))
        );
        const cfAccess = new Set<number>(
          (user.customFieldAccess ?? [])
            .map((a: any) => Number(a.customInputItemId))
            .filter((n: any): n is number => !Number.isNaN(n))
        );
        map.set(user.id, { branchIds, cfAccess, acceptsBranchless });
      }
    }
    return map;
  }, [rolesPipeline]);

  // Get selected leads from React Query cache (main + dynamic + board lists)
  const selectedLeads = useMemo(() => {
    if (selectedLeadIds.length === 0) return [];

    const allCachedLeads = extractLeadsFromLeadQueriesCache(queryClient);
    const selectedIdSet = new Set(selectedLeadIds.map(Number));
    return allCachedLeads.filter((l: any) => selectedIdSet.has(l.id));
  }, [selectedLeadIds, queryClient]);

  // True only when EVERY selected lead is present in the cache, so the
  // client-side branch/CF math below sees the full set. A "select all"
  // pulls thousands of IDs the infinite-scroll grid never rendered, leaving
  // most selected leads absent from the cache — in that case this is false
  // and the cache-derived per-user counts must NOT be trusted (the server
  // eligibility endpoint is authoritative at confirm time). This was the
  // root cause of the "935 skipped due to branch or permission" phantom:
  // uncached leads were silently treated as ineligible.
  const isComplete = useMemo(() => {
    if (selectedLeadIds.length === 0) return true;
    const uniqueSelected = new Set(selectedLeadIds.map(Number)).size;
    return selectedLeads.length >= uniqueSelected;
  }, [selectedLeads, selectedLeadIds]);

  // Compute eligibility counts per user (including single-lead selection for branch/CF gating)
  const eligibility = useMemo<EligibilityMap>(() => {
    if (selectedLeads.length === 0 || usersMap.size === 0) return {};

    const result: EligibilityMap = {};
    for (const [userId, { branchIds, cfAccess, acceptsBranchless }] of usersMap) {
      let count = 0;
      for (const lead of selectedLeads) {
        if (isLeadEligibleForUser(lead, branchIds, cfAccess, acceptsBranchless))
          count++;
      }
      result[userId] = count;
    }
    return result;
  }, [selectedLeads, usersMap]);

  const assignGateLead: AssignGateLead | null = useMemo(() => {
    if (selectedLeads.length !== 1) return null;
    const l = selectedLeads[0];
    return {
      branchId: l?.branchId ?? null,
      permissionItemIds: l?.permissionItemIds ?? [],
      permissionGates: l?.permissionGates ?? [],
    };
  }, [selectedLeads]);

  // Returns the actual eligible lead IDs for a specific user
  const eligibleLeadIds = useCallback(
    (userId: number): number[] => {
      const userData = usersMap.get(userId);
      if (!userData) return selectedLeadIds.map(Number);

      return selectedLeads
        .filter((lead) =>
          isLeadEligibleForUser(
            lead,
            userData.branchIds,
            userData.cfAccess,
            userData.acceptsBranchless
          )
        )
        .map((lead) => lead.id);
    },
    [selectedLeads, usersMap, selectedLeadIds]
  );

  return {
    totalLeads: selectedLeads.length || selectedLeadIds.length,
    eligibility,
    eligibleLeadIds,
    /**
     * False when the cache is missing some selected leads (e.g. after a
     * "select all"). Callers must suppress the cache-based per-user preview
     * and defer to the server eligibility endpoint when this is false.
     */
    isComplete,
    /** When exactly one lead is in cache: use for gating in UsersList (else pass gate from the drawer). */
    assignGateLead,
  };
}
